<template>
  <layout>
    <template v-slot:header>
      <h2>Create Inventory Order</h2>
      <AppHead title="Create Inventory Order" />
      <BreadCrumbs activeItem="Create" :crumbs="{ home: 'Dashboard', inventory: 'Inventory' }"/>
      <flash-messages />
    </template>

    <template v-slot:default>
        <div class="col-12 col-lg-6 px-0 mb-3">
          <card title="Inventory Info">
            <div class="mb-3">
              <jet-input id="order_number" placeholder="Order #" type="text" class="form-control" v-model="form.order_number"/>
              <jet-input-error :message="form.errors.order_number" class="mt-2" />
            </div>

            <div class="row">
              <Multiselect
                placeholder="Manufacturer"
                v-model="manufacturer_id"
                :options="manufacturers"
                :searchable="true"
                class="admin-multiselect mb-3 col-12 col-lg-6"
                id="manufacturer_select"
                @change="manufacturer"
              />

              <Multiselect
                v-model="categories_selected"
                mode="tags"
                :options="categories"
                :searchable="true"
                placeholder="Category"
                class="admin-multiselect mb-3 col-12 col-lg-6"
                id="category_select"
                @change="category"
              />
            </div>
          </card>
        </div>

        <Search :searchText="searchText" @searched="search"/>

        <DataTable ref="admin-create-inventory-order" :value="products" :paginator="true"
        :lazy="true"
        :first="(pagination.page - 1) * pagination.per_page"
        :rows="pagination.per_page"
        :totalRecords="pagination.total"
        @page="onPage($event)"
        :sortField="sortField"
        :sortOrder="sortOrder"
        @sort="onSort"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" removableSort
        paginatorPosition="both" currentPageReportTemplate="({first} - {last} of {totalRecords})"
        :rowsPerPageOptions="[10, 20, 50, 100]"
        breakpoint="992px"
        class="p-datatable-sm admin-datatable" tableClass="border-left border-right border-bottom" dataKey="id"
        v-model:selection="selectedProducts">

          <template #paginatorLeft>
            {{ products.length }} Products
          </template>
          <Column class="button-container" selectionMode="multiple" headerStyle="width: 3em"></Column>
          <Column field="name" header="Name" :sortable="true"></Column>
          <Column field="inventoryInfo" header="Inventory" :sortable="true" sortField="inventoryInfo.current_inventory">
            <template #body="slotProps">
              <InventoryInfo :inventoryInfo="slotProps.data.inventoryInfo" />
            </template>
          </Column>
          <Column field="sku" header="SKU" :sortable="true"></Column>
          <Column field="manufacturer_reference" header="Manufacturer Reference" :sortable="true"></Column>
          <Column field="upc" header="UPC" :sortable="true"></Column>
          <Column field="case_qty" header="Case QTY" :sortable="true"></Column>
        </DataTable>

        <card v-if="selectedProducts.length > 0" title="Included Products" class="mt-3">
          <p v-if="selectedProducts.length > 0" class="font-weight-bold">
            Total: ${{formatCurrency(getTotal(selectedProducts))}}
          </p>
          <div class="table-responsive">
            <table class="table table-striped">
              <thead>
              <tr>
                <th>Qty</th>
                <th>Cost per Item</th>
                <th>Name</th>
                <th>Inventory</th>
                <th>SKU</th>
                <th>Manufacturer Reference</th>
                <th>UPC</th>
                <th>Case QTY</th>
              </tr>
              </thead>
              <tbody>
                <tr v-for="product in selectedProducts" :key="product.id">
                  <td>
                    <input type="number" placeholder="qty" class="form-control custom-form-inline" @blur="setQty($event, product)"/>
                  </td>
                  <td>
                    <div class="input-group">
                      <span class="input-group-text" id="cost-addon">$</span>
                      <input type="number" class="form-control" v-model="product.cost_per_item" @blur="setCost($event, product)" aria-describedby="cost-addon"/>
                    </div>
                  </td>
                  <td class="m-text-nowrap">{{ product.name }}</td>
                  <td class="m-text-nowrap">
                    <InventoryInfo :inventoryInfo="product.inventoryInfo" />
                  </td>
                  <td class="m-text-nowrap">{{ product.sku }}</td>
                  <td>{{ product.manufacturer_reference }}</td>
                  <td>{{ product.upc }}</td>
                  <td>{{ product.case_qty }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </card>

        <div class="bottom-button">
          <jet-button class="px-5 mt-3" btnStyle="success" icon="fas fa-plus" :disable="form.processing" @click="submit()">Create</jet-button>
        </div>
    </template>
  </layout>
</template>

<script>
  import Layout from '@/Shared/Admin/Layout'
  import JetButton from '@/Jetstream/Button'
  import JetFormSection from '@/Jetstream/FormSection'
  import JetInput from '@/Jetstream/Input'
  import JetCheckbox from '@/Jetstream/Checkbox'
  import JetInputError from '@/Jetstream/InputError'
  import JetLabel from '@/Jetstream/Label'
  import JetActionMessage from '@/Jetstream/ActionMessage'
  import BreadCrumbs from '@/Shared/BreadCrumbs'
  import Multiselect from '@vueform/multiselect'
  import toastr from 'toastr'
  import Search from '@/Shared/Admin/Search'
  import InventoryInfo from '@/Components/General/InventoryInfo'

  export default {
    components: {
      JetActionMessage,
      JetButton,
      JetFormSection,
      JetInput,
      JetCheckbox,
      JetInputError,
      JetLabel,
      Layout,
      Multiselect,
      Search,
      BreadCrumbs,
      InventoryInfo,
    },
    props: {
      images: Object,
      manufacturers: Array,
      categories: Array,
      next_order_number: String
    },
    remember: 'form',
    data() {
      return {
        sortField: null, // current sort field
        sortOrder: null, // current sort order
        pagination: {
          total: 0,
          per_page: 20,
          page: 1,
        },
        products: [],
        searchText: "",
        categories_selected: [],
        manufacturers_selected: [],
        manufacturer_id: null,
        selectedProducts: [],
        productsInventory: [],
        form: this.$inertia.form({
          _method: 'put',
          order_number: this.next_order_number,
          manufacturer_id: null,
          products: null,
        }),
      }
    },
    methods: {
      category(selected) {
        this.$refs['admin-create-inventory-order'].resetPage()
        // control gets this event before model is updated
        this.categories_selected = selected;
        this.search(null, this.searchText)
      },
      getTotal(products) {
        let total = 0

        products.forEach(product => {
          if (product.cost_per_item && product.qty) total += (product.cost_per_item * product.qty)
        })
        
        return total
      },
      manufacturer(selected) {
        this.$refs['admin-create-inventory-order'].resetPage()
        this.manufacturer_id = selected

        this.search(null, this.searchText)
      },
      onPage(e) {
        this.pagination.page = e.page + 1
        this.pagination.per_page = e.rows
        this.search(null, this.searchText)
      },
      onSort(events) {
        this.sortField = events.sortField;
        this.sortOrder = events.sortOrder;
        this.search(null, this.searchText);
      },
      search(e, searchText) {
        this.searchText = searchText
        this.$refs['admin-create-inventory-order'].resetPage()
        axios.post(route('products.search'), {
          sort_field: this.sortField,
          sort_order: this.sortOrder,
          query: this.searchText,
          categories: this.categories_selected,
          manufacturer_id: this.manufacturer_id,
          include_inventory: 1
        }, {
          params: {
            page: this.pagination.page, per_page: this.pagination.per_page
          }
        }).then((response) => {
          if (response.data.success) {
            this.products = response.data.data;
            this.pagination.total = response.data.total
          }
        });
      },
      selected(product) {
        for (var i=0; i<this.selectedProducts.length; i++) {
          if (this.selectedProducts[i].id == product.id) return i;
        }

        return -1;
      },
      addProduct(product) {
        if (this.selected(product) > -1) {

        } else {
          this.selectedProducts[this.selectedProducts.length] = product;
        }
      },
      setQty(e, product) {
        this.selectedProducts[this.selected(product)].qty = e.target.value;
      },
      setCost(e, product) {
        this.selectedProducts[this.selected(product)].cost_per_item = e.target.value;
      },
      submit() {
        let submitProducts = [];

        for (var i=0; i<this.selectedProducts.length; i++) {
          if (!this.selectedProducts[i].qty || this.selectedProducts[i].qty < 1) {
            toastr.error("Missing Qty.");
            return;
          }
          if (!this.selectedProducts[i].cost_per_item || this.selectedProducts[i].cost_per_item < 1) {
            toastr.error("Missing Cost.");
            return;
          }
          submitProducts[i] = {
            id: this.selectedProducts[i].id,
            qty: this.selectedProducts[i].qty,
            cost_per_item: this.selectedProducts[i].cost_per_item
          }
        }

        this.form.products = JSON.stringify(submitProducts);
        this.form.manufacturer_id = this.manufacturer_id
        this.form.post(this.route('inventory.store'))
      }
    },
  }
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
